import {
  Anchor,
  Button,
  Card,
  Center,
  Divider,
  Stack,
  ThemeIcon,
  Title,
  TypographyStylesProvider,
  Image,
} from "@mantine/core";

import React from "react";
import DocsHighlightCard from "../articlesHelpers/DocsHighlightCard";
import { IconFileInvoice } from "@tabler/icons-react";
import DocsButton from "../articlesHelpers/DocsButton";
import SimpleImage from "../articlesHelpers/SimpleImage";

export const RECEIVE_TO_LIGHTNING_INVOICE_CONTENT = (
  <TypographyStylesProvider>
    <p>
      {" "}
      The oldest and most widely compatible method with which you can receive
      lightning payments is the BOLT 11 invoice, which is described in the{" "}
      <a href="https://github.com/lightning/bolts/blob/master/11-payment-encoding.md">
        Bolt 11 specification
      </a>
      .
    </p>{" "}
    The workflow for a BOLT 11 invoices is as follows:
    <ol>
      <li> You generate an invoice</li>
      <li> You provide the invoice to the person who will pay you</li>
      <li> They pay the invoice</li>
      <li> You receive the payment</li>
    </ol>
    <p>
      The <strong>most important</strong> thing to understand about BOLT 11
      invoices is that they are <strong>single use</strong> and have an{" "}
      <strong>expiration date & time</strong>.
    </p>
    <p>
      <strong> Single use: </strong> If used once, they can no longer be used
      again.
    </p>
    <p>
      {" "}
      <strong>Expiration date & time:</strong> They can never be used after they
      have expired.
    </p>
    <p>This button will get you started with invoice generation: </p>
    <DocsButton
      ariaLabel="receive to lightning invoice"
      text="Receive To Invoice"
      leftSection={<IconFileInvoice />}
    />
    <p> You'll then see this dialog:</p>
    <SimpleImage h={450} src="docs_images/make-lightning-invoice.png" />
    <p>Let's discuss the options here, one by one.</p>
    <p>
      The <strong>Maximum</strong>, in this case 393,043 satoshis, is the
      maximum amount Rizful Node will allow you to make an invoice for. This is
      to prevent you from making an invoice for an amount which exceeds your
      available inbound capacity.
    </p>
    <p>
      The <strong>Satoshis</strong> amount specifies how much your payee will
      send to you. You can actually not specify a value, and leave the amount at
      `0`, which will allow your payee to send you any amount they wish. There
      are some problems with this approach however, like, for example, what if
      your payee sends you an amount that exceeds your inbound capacity? In that
      case, the payment will fail.
    </p>
    <p>
      So, in most cases, you should specify a <strong>Satoshis</strong> amount,
      and when your payee pays the invoice, they will see that amount on their
      screen.
    </p>
    <p>
      <strong>Expires in</strong> specifies at what date the invoice will
      expire. Choose as long a date as possible, to give your payee flexibility.
    </p>
    <p>
      <strong> Description </strong> is a short text that will be displayed to
      your payee when they pay the invoice.
    </p>
    <p>
      The checkbox for <strong>Private</strong> is important to understand.{" "}
    </p>{" "}
    <p>
      Your options regarding a <strong>Private</strong> versus a{" "}
      <strong> Public </strong> invoice are limited by the KIND of lightning
      channels your node CURRENTLY has open. If your channels are all private,
      you can only generate <strong>Private</strong> invoices. If all your
      channel are public, you'll only be able to generate{" "}
      <strong>Public </strong>invoices. If you have a mix of private and public
      channels, you can choose between the two.
    </p>
    <p>
      Private invoices have the nice property that they conceal your node public
      key. They're not perfect, and the community is working on a new BOLT 12
      standard that will improve on this, but for now, they're the best we have.
    </p>
    <p>
      <strong>Blinded route</strong> is still experimental, but in the future
      should help to improve privacy for public invoices. Only use blinded route
      invoices for testing, not for real payments.{" "}
    </p>
    <p> After you specify the options, you can then click this button:</p>
    <DocsButton
      ariaLabel="make invoice"
      text="Make Invoice"
      leftSection={<IconFileInvoice />}
    />
    <p> And then you'll see this dialog:</p>
    <SimpleImage
      h={300}
      src="docs_images/make-invoice-qr-and-copy-string.png"
    />
    <p>There are two ways to provide the invoice to your payee:</p>
    <ol>
      <li>
        Take a screenshot of the QR code and send it to them. They can scan the
        QR code with their lightning wallet.
      </li>
      <li>
        Copy the text string and send it to them. They can paste the text string
        into their lightning wallet.
      </li>
    </ol>
  </TypographyStylesProvider>
);
